import { useEffect, useMemo, useRef, useState } from 'react';
import { PaginatedResponse, PaginationQueries } from 'types/search.type';
import { VehicleSummary } from 'types/vehicle.type';
import { useComponentDidUpdate } from './useComponentDidUpdate';
import { usePagination } from './usePagination';
import {
  ExperienceListSortOption,
  VehicleListSortOption,
} from 'enums/vehicleListSortOption';
import * as NProgress from 'nprogress';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next-translate-routes';
import { useFilterStore } from 'states/useFilterStore';
import { searchService } from 'services/SearchService';
import { VehicleType } from 'enums/vehicleTypes';
import { useSearchFromQuery } from './useSearchFromQuery';
import { ExperienceSummary } from 'types/experience/experience-summary.type';

export function useVehicleList(
  defaultVehicles: VehicleSummary[] | ExperienceSummary[],
  isLastPage: boolean,
  apiTotalElements: number,
  {
    defaultVehicleType,
    isExperience,
  }: {
    defaultVehicleType: VehicleType | undefined;
    isExperience: boolean;
  }
) {
  const { t } = useTranslation();
  const router = useRouter();
  const { pagination, incrementPage } = usePagination();
  const [vehicleList, setVehicleList] =
    useState<(VehicleSummary | ExperienceSummary)[]>(defaultVehicles);
  const isRouteChange = useRef<boolean>(false);
  const { filterParams } = useFilterStore();
  const [totalElements, setTotalElements] = useState<number>(apiTotalElements);
  const { getStateFromQuery } = useSearchFromQuery();

  const [sortOption, setSortOption] = useState<VehicleListSortOption>(
    VehicleListSortOption.BASE
  );

  const [isThereMorePage, setIsThereMorePage] = useState<boolean>(!isLastPage);

  const getVehiclesAndCheckPage = async (isSort = false) => {
    if (isRouteChange.current) return;
    try {
      let paginationParams: PaginationQueries = {
        page: pagination.page,
        size: pagination.size,
        sort: [sortOption].filter((k) => k),
      };
      if (isSort) {
        paginationParams = {
          page: 0,
          size: pagination.size * (pagination.page + 1),
          sort: [sortOption].filter((k) => k),
        };
      }

      const { location, ...searchQueries } = getStateFromQuery();

      NProgress.start();
      let response:
        | PaginatedResponse<VehicleSummary[]>
        | PaginatedResponse<ExperienceSummary[]>;

      if (isExperience) {
        const { experiences } =
          await searchService.getCompleteExperienceListWithFilter(
            paginationParams,
            {
              ...(location?.slug ? { locationSlug: location.slug } : {}),
              ...searchQueries,
            }
          );
        response = experiences;
      } else {
        const { vehicles } =
          await searchService.getCompleteVehicleListWithFilter(
            paginationParams,
            {
              ...(location?.slug ? { locationSlugs: [location.slug] } : {}),
              ...filterParams,

              vehicleTypes: [
                ...(defaultVehicleType ? [defaultVehicleType] : []),
                ...(filterParams.vehicleTypes || []),
              ],
            },
            { ...searchQueries }
          );
        response = vehicles;
      }

      if (isSort) {
        setVehicleList([...response.content]);
      } else {
        setVehicleList((val) => [...val, ...response.content]);
      }

      setTotalElements(response.totalElements);
      setIsThereMorePage(!response.last);
    } finally {
      NProgress.done();
    }
  };

  useEffect(() => {
    setVehicleList(defaultVehicles);
  }, [defaultVehicles]);

  useEffect(() => {
    setIsThereMorePage(!isLastPage);
  }, [isLastPage]);

  useEffect(() => {
    setTotalElements(apiTotalElements);
  }, [apiTotalElements]);

  useComponentDidUpdate(() => {
    getVehiclesAndCheckPage();
  }, [pagination.page]);

  useComponentDidUpdate(() => {
    getVehiclesAndCheckPage(true);
  }, [sortOption]);

  useComponentDidUpdate(() => {
    getVehiclesAndCheckPage(true);
  }, [filterParams]);

  useEffect(() => {
    const onRouteChangeStart = () => {
      isRouteChange.current = true;
      setSortOption(VehicleListSortOption.BASE);
    };
    const onRouteChangeCompleted = () => {
      isRouteChange.current = false;
    };
    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeCompleted);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeCompleted);
    };
  }, []);

  const sortOptions: {
    label: string;
    value: VehicleListSortOption | ExperienceListSortOption;
  }[] = useMemo(() => {
    return [
      {
        label: t('search:sort.BASE'),
        value: isExperience
          ? ExperienceListSortOption.BASE
          : VehicleListSortOption.BASE,
      },
      {
        label: t('search:sort.PRICE_ASC'),
        value: isExperience
          ? ExperienceListSortOption.PRICE_ASC
          : VehicleListSortOption.PRICE_ASC,
      },
      {
        label: t('search:sort.PRICE_DESC'),
        value: isExperience
          ? ExperienceListSortOption.PRICE_DESC
          : VehicleListSortOption.PRICE_DESC,
      },
    ];
  }, [t]);

  return {
    incrementPage,
    vehicleList,
    isThereMorePage,
    sortOption,
    setSortOption,
    sortOptions,
    totalElements,
  };
}
